import { useTranslation } from 'react-i18next';
import { AlertTriangle, X } from 'lucide-react';
import { cn, Dialog, DialogContent, DialogTitle, DialogClose, Button } from '@scio3/ui';

type ChangePlanWarning = {
  messageDisplay?: string;
  textValues?: string[];
  requiredRemove?: boolean;
  url?: string;
  plan?: string;
};

type Props = {
  open: boolean;
  warnings: ChangePlanWarning[];
  onDismiss: () => void;
  onConfirm: () => void;
  /** Custom label for the confirm button (default: 'Next') */
  confirmLabel?: string;
};

/**
 * Plan change warning dialog — 1:1 port of scio1 ConfirmDowngradeSubscriptionModal
 * (`scio10/imports/ui/components/app/subscription/confirmDowngradeSubscriptionModal.{js,html}`).
 *
 * scio1 button rule (line 33: `if (settings.showOK) settings.showCancel = false`):
 *   - Default → only **Next** is shown (X in header is the dismiss).
 *   - Any warning has `requiredRemove: true` → Next is hidden, **Close** is shown instead.
 *
 * Backend `validateChangePlan` provides the warnings array with messageDisplay,
 * textValues, requiredRemove, optional `url` (for "Go To" link), and `plan` (interpolated into messageDisplay).
 */
export function ChangePlanWarningDialog({
  open,
  warnings,
  onDismiss,
  onConfirm,
  confirmLabel,
}: Props) {
  const { t } = useTranslation();

  const hasRequiredRemove = warnings.some((w) => w.requiredRemove);
  // Mirror scio1: showOK XOR showCancel — never both visible at once.
  const showOK = !hasRequiredRemove;
  const showCancel = !showOK;

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) onDismiss();
      }}
    >
      <DialogContent
        className="flex max-h-[85vh] flex-col gap-0 overflow-hidden p-0 sm:max-w-md"
        hideCloseButton
        data-testid="change-plan-warning-dialog"
      >
        {/* Amber warning header — scio1 bg-warning + fa-exclamation-triangle */}
        <div className="flex items-center justify-between bg-amber-500 px-5 py-3 text-white">
          <DialogTitle className="flex items-center gap-2 text-base font-semibold text-white">
            <AlertTriangle className="h-5 w-5 shrink-0" />
            {t('Plan Change Warning')}
          </DialogTitle>
          <DialogClose
            className={cn(
              'flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-md',
              'text-white/80 transition-colors',
              'hover:bg-white/15 hover:text-white',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60',
            )}
            aria-label={t('Close')}
          >
            <X className="h-4 w-4" />
          </DialogClose>
        </div>
        <div className="flex-1 space-y-3 overflow-y-auto px-5 py-4">
          {warnings.map((warning, idx) => (
            <div key={idx}>
              <p className="text-sm font-semibold">
                {t(warning.messageDisplay ?? '', { plan: warning.plan ?? '' })}
                {/* "Go To" button — scio1 only renders when both url and requiredRemove are set */}
                {warning.requiredRemove && warning.url && (
                  <a
                    href={warning.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="ml-2 inline-block"
                  >
                    <Button size="sm" className="cursor-pointer">
                      {t('Go To')}
                    </Button>
                  </a>
                )}
              </p>
              {warning.textValues && warning.textValues.length > 0 && (
                <ul className="mt-2 space-y-0.5 pl-1">
                  {warning.textValues.map((text, i) => (
                    <li key={i} className="text-sm text-muted-foreground">
                      - {t(text)}
                    </li>
                  ))}
                </ul>
              )}
              {idx < warnings.length - 1 && <hr className="mt-3" />}
            </div>
          ))}
        </div>
        <div className="flex justify-end gap-2 border-t border-border bg-muted/30 px-5 py-3">
          {showCancel && (
            <Button variant="outline" onClick={onDismiss} className="cursor-pointer">
              {t('Close')}
            </Button>
          )}
          {showOK && (
            <Button onClick={onConfirm} className="cursor-pointer">
              {confirmLabel ?? t('Next')}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
